/**
 * Shopify Embed — Home page (status surface only).
 *
 * Per 2026-05-15 direction: "everything happens in commerce studio
 * platform once they're signed in and synched their products". The
 * Shopify embedded admin is intentionally a two-step onboarding (sign
 * in + sync) followed by a status-only landing page. No app catalog,
 * no enable/disable toggles, no per-widget configuration, no analytics
 * dashboard. Single source of truth = commerce.varai.ai. See
 * [[feedback_plugin_single_source_of_truth]].
 *
 * The merchant's only Shopify-side action after onboarding is
 * re-syncing products if their catalog drifts. Everything else —
 * installing widgets, configuring styling, viewing analytics,
 * activating in theme — lives in Commerce Studio.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Badge,
  Banner,
  InlineStack,
  BlockStack,
  Box,
  Spinner,
  Icon,
} from '@shopify/polaris';
import {
  RefreshIcon,
  ExternalIcon,
  CheckCircleIcon,
  AlertCircleIcon,
} from '@shopify/polaris-icons';
import { useAuthenticatedFetch } from '../App';

type ConnectionStatus = 'connected' | 'disconnected' | 'unknown';

interface CredentialsPayload {
  success: boolean;
  shop?: string;
  tenant_id?: string | null;
  tenant_name?: string | null;
  region?: string | null;
  last_sync_at?: string | null;
  error?: string;
  message?: string;
}

const DEFAULT_DASHBOARD_URL = 'https://commerce.varai.ai';

function formatLastSync(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return iso;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function getShopFromUrl(): string {
  return new URLSearchParams(window.location.search).get('shop') || '';
}

export default function HomePage() {
  const authenticatedFetch = useAuthenticatedFetch();
  const shop = getShopFromUrl();

  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [region, setRegion] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('unknown');
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [dashboardUrl, setDashboardUrl] = useState<string>(DEFAULT_DASHBOARD_URL);

  // ── Load connection state ───────────────────────────────────────────
  const loadStatus = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await authenticatedFetch('/api/commerce-studio/credentials');
      const data: CredentialsPayload = await res.json();
      if (res.ok && data.success) {
        setTenantId(data.tenant_id || null);
        setTenantName(data.tenant_name || null);
        setRegion(data.region || null);
        setLastSyncAt(data.last_sync_at || null);
        setConnectionStatus(data.tenant_id ? 'connected' : 'disconnected');
      } else {
        setConnectionStatus('disconnected');
        setErrorMessage(data.error || data.message || 'Could not verify connection to Commerce Studio.');
      }
    } catch (err) {
      console.error('[Home] load status failed:', err);
      setConnectionStatus('disconnected');
      setErrorMessage('Could not reach Commerce Studio. Check your connection and refresh.');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const fetchDashboardUrl = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/api/commerce-studio/dashboard-url');
      const data = await res.json();
      if (data?.url) setDashboardUrl(data.url);
    } catch {
      // Keep DEFAULT_DASHBOARD_URL on failure.
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    loadStatus();
    fetchDashboardUrl();
  }, [loadStatus, fetchDashboardUrl]);

  // ── Sync products (the one Shopify-side action) ─────────────────────
  const handleSyncProducts = useCallback(async () => {
    setIsSyncing(true);
    setErrorMessage(null);
    setInfoMessage(null);
    try {
      const res = await authenticatedFetch(
        `/api/commerce-studio/sync-products?shop=${encodeURIComponent(shop)}`,
        { method: 'POST' }
      );
      const data = await res.json();
      if (data?.success) {
        setInfoMessage(data.message || 'Product sync started.');
        // Refresh status to pick up the new last_sync_at when it lands.
        loadStatus();
      } else {
        setErrorMessage(data?.error || 'Product sync failed.');
      }
    } catch (err) {
      console.error('[Home] sync failed:', err);
      setErrorMessage('Could not start product sync.');
    } finally {
      setIsSyncing(false);
    }
  }, [authenticatedFetch, shop, loadStatus]);

  const openDashboard = useCallback(() => {
    window.open(`${dashboardUrl}?shop=${encodeURIComponent(shop)}&source=shopify`, '_blank');
  }, [dashboardUrl, shop]);

  // ── Render ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Page title="Commerce Studio">
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="600">
                <InlineStack gap="300" blockAlign="center">
                  <Spinner accessibilityLabel="Loading connection status" size="small" />
                  <Text as="span" variant="bodyMd">Checking connection…</Text>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const isConnected = connectionStatus === 'connected';

  return (
    <Page
      title="Commerce Studio"
      subtitle="Your Shopify store is connected. Manage everything else in Commerce Studio."
      primaryAction={{
        content: 'Open Commerce Studio',
        onAction: openDashboard,
        icon: ExternalIcon,
      }}
      secondaryActions={[
        {
          content: isSyncing ? 'Syncing…' : 'Re-sync products',
          onAction: handleSyncProducts,
          icon: RefreshIcon,
          loading: isSyncing,
          disabled: isSyncing || !isConnected,
        },
      ]}
    >
      <Layout>
        {infoMessage && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setInfoMessage(null)}>
              <p>{infoMessage}</p>
            </Banner>
          </Layout.Section>
        )}
        {errorMessage && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setErrorMessage(null)}>
              <p>{errorMessage}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Connection status card */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <InlineStack gap="200" blockAlign="center">
                  <Icon
                    source={isConnected ? CheckCircleIcon : AlertCircleIcon}
                    tone={isConnected ? 'success' : 'critical'}
                  />
                  <Text as="h2" variant="headingMd">
                    {isConnected ? 'Connected to Commerce Studio' : 'Not connected'}
                  </Text>
                  {isConnected && <Badge tone="success">Active</Badge>}
                </InlineStack>

                {isConnected ? (
                  <BlockStack gap="200">
                    {tenantName && (
                      <InlineStack gap="200">
                        <Text as="span" variant="bodyMd" tone="subdued">Tenant:</Text>
                        <Text as="span" variant="bodyMd">{tenantName}</Text>
                      </InlineStack>
                    )}
                    {region && (
                      <InlineStack gap="200">
                        <Text as="span" variant="bodyMd" tone="subdued">Region:</Text>
                        <Text as="span" variant="bodyMd">{region}</Text>
                      </InlineStack>
                    )}
                    {tenantId && (
                      <InlineStack gap="200">
                        <Text as="span" variant="bodyMd" tone="subdued">Tenant ID:</Text>
                        <Text as="span" variant="bodyMd">
                          <code style={{ fontSize: 12 }}>{tenantId}</code>
                        </Text>
                      </InlineStack>
                    )}
                  </BlockStack>
                ) : (
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Your Shopify store is not connected to Commerce Studio. Try re-installing the app or contact support.
                  </Text>
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Product sync card — the one Shopify-side action */}
        {isConnected && (
          <Layout.Section>
            <Card>
              <Box padding="400">
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">Product sync</Text>
                  <InlineStack gap="200">
                    <Text as="span" variant="bodyMd" tone="subdued">Last synced:</Text>
                    <Text as="span" variant="bodyMd">{formatLastSync(lastSyncAt)}</Text>
                  </InlineStack>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Products sync automatically on creation, update, and delete. Use Re-sync if your Commerce Studio catalog looks stale.
                  </Text>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        )}

        {/* Where everything else happens — single CTA */}
        {isConnected && (
          <Layout.Section>
            <Card>
              <Box padding="400">
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">Everything else lives in Commerce Studio</Text>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Browse and enable widgets, configure styling, view analytics, and activate widgets in your theme — all from the Commerce Studio dashboard.
                  </Text>
                  <Box>
                    <InlineStack gap="200">
                      <Badge tone="info">Manage widgets</Badge>
                      <Badge tone="info">Configure styling</Badge>
                      <Badge tone="info">View analytics</Badge>
                      <Badge tone="info">Activate in theme</Badge>
                    </InlineStack>
                  </Box>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
